import { useEffect, useMemo, useState } from 'react'
import { getWorkstreamCards, type VaultNode } from '@/lib/api'
import { useAutoRefresh } from './useAutoRefresh'
import { ExtTag } from './vaultUi'

type Sort = 'newest' | 'oldest' | 'az' | 'za'

/** The project page's Cards section: EVERY card under the workstream,
 * recursively (loose + inside blocks + archived) — the Obsidian model. Search +
 * filetype filter + sort, client-side over the one aggregated query. */
export function WorkstreamCards({ nodeId, onSelect }: { nodeId: string; onSelect: (id: string) => void }) {
  const [cards, setCards] = useState<VaultNode[]>([])
  const [loaded, setLoaded] = useState(false)
  const [q, setQ] = useState('')
  const [type, setType] = useState('*')
  const [sort, setSort] = useState<Sort>('newest')

  useEffect(() => {
    let cancelled = false
    setLoaded(false)
    setQ('')
    setType('*')
    getWorkstreamCards(nodeId)
      .then((r) => {
        if (!cancelled) {
          setCards(r.cards)
          setLoaded(true)
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [nodeId])

  // Background freshness: new cards (e.g. an email just dropped into a block)
  // appear without a reload. Silent — preserves the search/type/sort controls.
  useAutoRefresh(() => {
    getWorkstreamCards(nodeId)
      .then((r) => setCards(r.cards))
      .catch(() => {})
  })

  const types = useMemo(
    () => (Array.from(new Set(cards.map((c) => c.ext).filter(Boolean))) as string[]).sort(),
    [cards]
  )

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase()
    const ts = (c: VaultNode) => (c.created_ts ? Date.parse(c.created_ts) : 0)
    const list = cards.filter(
      (c) => (type === '*' || c.ext === type) && (!query || c.name.toLowerCase().includes(query))
    )
    switch (sort) {
      case 'oldest':
        return [...list].sort((a, b) => ts(a) - ts(b))
      case 'az':
        return [...list].sort((a, b) => a.name.localeCompare(b.name))
      case 'za':
        return [...list].sort((a, b) => b.name.localeCompare(a.name))
      default:
        return [...list].sort((a, b) => ts(b) - ts(a))
    }
  }, [cards, q, type, sort])

  if (loaded && cards.length === 0) return null

  return (
    <div className="mt-6">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Cards <span className="font-normal text-muted-foreground/70">{cards.length}</span>
      </div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search cards…"
          className="min-w-[180px] flex-1 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="rounded-md border border-border bg-muted/40 px-2 py-1.5 text-[12px] text-foreground focus:outline-none"
        >
          <option value="*">All types</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {t.replace('.', '').toUpperCase()}
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as Sort)}
          className="rounded-md border border-border bg-muted/40 px-2 py-1.5 text-[12px] text-foreground focus:outline-none"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="az">A → Z</option>
          <option value="za">Z → A</option>
        </select>
      </div>
      <div className="flex flex-col divide-y divide-border overflow-hidden rounded-md border border-border">
        {filtered.map((c) => (
          <button
            key={c.id}
            onClick={() => onSelect(c.id)}
            className="flex items-center gap-2 px-3 py-2 text-left text-[13px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          >
            <ExtTag ext={c.ext} />
            <span className="truncate">{c.name}</span>
            {c.is_archived && <span className="ml-auto shrink-0 text-[10px] opacity-60">📦</span>}
          </button>
        ))}
        {filtered.length === 0 && <div className="px-3 py-2 text-[12px] text-muted-foreground">No cards match</div>}
      </div>
    </div>
  )
}
